// Layered, deterministic validator of the Creative Document (CREATIVE-MODEL-V1.md §13).
//
//   precheck (plain JSON, size, depth, schema version, PII intent)
//   → L1 structure (strict schema)
//   → L2 references
//   → L3 animation · interaction · survey · forms · game
//   → L4 policy (injected ResourcePolicy) + publication completeness
//
// Pure function of (input, options). Same input ⇒ same issues in the same
// order. Never calls the network, a clock, randomness or an AI model, and
// never repairs input: it reports. Used identically by the editor, the API,
// the compiler and AI candidate checks (ADR-0018 §2.1-6).

import { FAIL } from "../schema/dsl.js";
import { creativeDocument, type CreativeDocument } from "../schema/document.js";
import { IssueSink, sortIssues, type EntityKind, type ValidationIssue } from "../issues.js";
import { isPlainObject } from "../schema/dsl.js";
import { measure } from "../analysis.js";
import { RESOURCE_LIMIT_KEYS, UNRESTRICTED_POLICY, isValidPolicy, type ResourcePolicy } from "../policy.js";
import { buildIndex } from "./context.js";
import { checkPiiAttempts, checkPlainJson, checkSchemaVersion } from "./precheck.js";
import { validateReferences } from "./references.js";
import { validateAnimation } from "./animation.js";
import { validateInteraction } from "./interaction.js";
import { validateForms, validateSurvey } from "./survey.js";
import { validateGame, validateGameModule } from "./game.js";
import { validateComponents, validateFormPresentation } from "./components.js";

export interface ValidateOptions {
  /** Effective resource policy (see combinePolicies). Default: no limit beyond L0 ceilings. */
  readonly policy?: ResourcePolicy;
  /** Distribution formats that must each be served by exactly one layout variant. */
  readonly requiredFormats?: readonly string[];
}

export interface ValidationResult {
  /** No error in any layer: the document may leave DRAFT / be compiled. */
  readonly ok: boolean;
  /** No structure or reference error: the document may be saved as a DRAFT (L3–L4 issues allowed). */
  readonly canSave: boolean;
  /** The parsed document (only declared keys) when the structure is valid. */
  readonly document?: CreativeDocument;
  readonly issues: readonly ValidationIssue[];
}

const COLLECTIONS: ReadonlyArray<readonly [string, EntityKind, string]> = [
  ["layouts", "layout", "id"],
  ["scenes", "scene", "id"],
  ["elements", "element", "id"],
  ["assets", "asset", "key"],
  ["fonts", "font", "key"],
  ["links", "link", "id"],
  ["variables", "variable", "id"],
  ["timers", "timer", "id"],
  ["rules", "rule", "id"],
  ["events", "event", "name"],
  ["forms", "form", "id"],
];

/** Attaches { kind, id } of the entity an issue path points into, when it can be read safely. */
function withEntity(issue: ValidationIssue, raw: unknown): ValidationIssue {
  if (issue.entity || !isPlainObject(raw)) return issue;
  const question = /^\$\.survey\.questions\[(\d+)\]/.exec(issue.path);
  if (question && isPlainObject(raw.survey) && Array.isArray(raw.survey.questions)) {
    const q: unknown = raw.survey.questions[Number(question[1])];
    if (isPlainObject(q) && typeof q.id === "string" && q.id.length <= 64)
      return { ...issue, entity: { kind: "question", id: q.id } };
  }
  if (issue.path.startsWith("$.survey")) {
    const s = raw.survey;
    if (isPlainObject(s) && typeof s.id === "string" && s.id.length <= 64)
      return { ...issue, entity: { kind: "survey", id: s.id } };
  }
  if (issue.path.startsWith("$.game")) {
    const g = raw.game;
    if (isPlainObject(g) && typeof g.id === "string" && g.id.length <= 64)
      return { ...issue, entity: { kind: "game", id: g.id } };
  }
  if (issue.path.startsWith("$.fallback")) return { ...issue, entity: { kind: "fallback", id: "fallback" } };
  for (const [collection, kind, key] of COLLECTIONS) {
    const m = new RegExp(`^\\$\\.${collection}\\[(\\d+)\\]`).exec(issue.path);
    if (!m) continue;
    const list = raw[collection];
    if (!Array.isArray(list)) return issue;
    const item: unknown = list[Number(m[1])];
    if (isPlainObject(item) && typeof item[key] === "string" && item[key].length <= 64)
      return { ...issue, entity: { kind, id: item[key] } };
    return issue;
  }
  return issue;
}

function finish(sink: IssueSink, raw: unknown, document?: CreativeDocument): ValidationResult {
  const issues = sortIssues(sink.issues.map((i) => withEntity(i, raw)));
  const errors = issues.filter((i) => i.severity === "error");
  return {
    ok: errors.length === 0 && document !== undefined,
    canSave: document !== undefined && !errors.some((i) => i.layer === "structure" || i.layer === "reference"),
    ...(document ? { document } : {}),
    issues,
  };
}

export function validateCreativeDocument(input: unknown, options: ValidateOptions = {}): ValidationResult {
  const policy = options.policy ?? UNRESTRICTED_POLICY;
  if (!isValidPolicy(policy)) throw new TypeError("validateCreativeDocument: invalid ResourcePolicy");

  const structure = new IssueSink("structure");
  if (!checkPlainJson(input, structure) || !checkSchemaVersion(input, structure)) return finish(structure, undefined);
  checkPiiAttempts(input, structure.forLayer("form"));

  const parsed = creativeDocument.parse(input, "$", structure);
  if (parsed === FAIL) return finish(structure, input);
  const doc = parsed;

  const index = buildIndex(doc);
  const reference = structure.forLayer("reference");
  validateReferences(index, reference, { requiredFormats: options.requiredFormats });
  if (doc.html5) {
    const project = doc.html5;
    const layout = doc.layouts.find((l) => l.id === project.layoutId);
    const paths = project.files.map((f) => f.path);
    if (!layout || layout.sizing.mode !== "fixed" || layout.sizing.width !== project.width || layout.sizing.height !== project.height || !paths.includes(project.entryPoint) || new Set(paths).size !== paths.length ||
        project.files.some((f) => f.path.split("/").length > 9) ||
        project.files.reduce((n, f) => n + f.bytes, 0) > 30 * 1024 * 1024)
      reference.add("INVALID_VALUE", "$.html5", "Invalid HTML5 layout, entry point, file set or aggregate size.", {});
  }
  if (reference.errorCount > 0) return finish(structure, input, doc);

  validateAnimation(index, structure.forLayer("animation"));
  validateInteraction(index, structure.forLayer("interaction"));
  validateSurvey(index, structure.forLayer("survey"));
  validateForms(index, structure.forLayer("form"));
  validateFormPresentation(index, structure.forLayer("form"));
  validateComponents(index, structure.forLayer("interaction"));
  validateGame(index, structure.forLayer("game"));
  validateGameModule(index, structure.forLayer("game"));

  const policySink = structure.forLayer("policy");
  // Publication of an AI-generated HTML5 project (ADR-0043): only once it passed the isolated
  // browser preview; a project still pending its runner/browser check cannot leave DRAFT.
  if (doc.html5 && doc.html5.validation !== "isolated-preview")
    policySink.add("HTML5_EXECUTION_NOT_APPROVED", "$.html5", "HTML5 execution requires an independently approved runner and browser.", {});
  // The static fallback image is OPTIONAL (owner decision 2026-10-25, ADR-0042): the client publishes
  // the interactive rich-media ad. FALLBACK_MISSING stays in the closed code list but is never emitted.
  const measures = measure(index);
  for (const key of RESOURCE_LIMIT_KEYS) {
    const max = policy.limits[key];
    const actual = measures.counts[key];
    if (max !== undefined && actual !== undefined && actual > max)
      policySink.add(
        "POLICY_LIMIT_EXCEEDED",
        "$",
        `Limit "${key}" of policy ${policy.id}@${String(policy.version)} exceeded (${String(actual)} > ${String(max)}).`,
        {
          limit: key,
          max,
          actual,
          policy: `${policy.id}@${String(policy.version)}`,
        }
      );
  }
  return finish(structure, input, doc);
}
